import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  CheckCircle2,
  Compass,
  HelpCircle,
  Layers3,
  Lightbulb,
  Menu,
  Route,
  Scale,
  TriangleAlert,
  Waypoints,
  X } from
"lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LeadCaptureDialog from "@/components/pivotbox/LeadCaptureDialog";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import StickyCTA from "@/components/pivotbox/StickyCTA";
import BuiltOnScienceSection from "@/components/pivotbox/BuiltOnScienceSection";
import {
  brandAssets,
  contactEmail,
  diagnosticOutputs,
  founderStory,
  heroStats,
  heroSupport,
  pivotTypes,
  problemQuestions,
  quote } from
"@/components/pivotbox/siteContent";

const sectionLinks = [
{ label: "Pivotbox Framework", href: "framework" },
{ label: "How It Works", href: "how-it-works" },
{ label: "About us", href: "about" },
{ label: "Take the Founder Signal Test — Free", href: "founder-signal-test", cta: true },
{ label: "Contact us", href: "site-footer" }];


const iconMap = [Compass, Layers3, Scale, Route, Waypoints, Lightbulb];
const heroSectionImage = "/assets/pivotbox/hero-crossroads.png";

const decisionDrivers = [
{
  title: "Instinct",
  text: "The desire to build often begins as a feeling, but feelings alone rarely produce a reliable founder decision."
},
{
  title: "Excitement",
  text: "Momentum can make entrepreneurship look simpler than it is, especially before trade-offs are mapped clearly."
},
{
  title: "Fear",
  text: "Risk concerns become more manageable when they are surfaced through structure rather than avoided altogether."
}];


const alignmentPillars = [
{
  title: "Motivation",
  text: "Clarifies the energy behind the move and whether it is durable enough to sustain founder work."
},
{
  title: "Life readiness",
  text: "Looks at personal and professional conditions required to support a transition into entrepreneurship."
},
{
  title: "Capability fit",
  text: "Evaluates whether current strengths and gaps align with the demands of building something independently."
},
{
  title: "Strategic direction",
  text: "Helps define the type of entrepreneurial path most suited to your context, timing, and ambition."
}];


const founderHighlights = [
{
  value: "~20 years",
  label: "across corporate leadership and entrepreneurship"
},
{
  value: "15+ years",
  label: "with organizations such as Oracle, Publicis Sapient, and GMR Group"
}];

const testRevealInsights = [
{
  icon: Lightbulb,
  title: "What’s driving you",
  text: "Understand the real reason entrepreneurship is appealing to you, whether it’s frustration with your current job, a desire for meaningful work, ambition to build something bigger, or the need to test an idea carefully before taking the leap."
},
{
  icon: Compass,
  title: "Your pivot type",
  text: "Discover your Pivot Type, Seeker, Evolver, Aspirer, or Navigator and what that reveals about how you naturally approach the move toward Entrepreneurship."
},
{
  icon: TriangleAlert,
  title: "Your potential blind spot",
  text: "Every entrepreneurial type has a common blind spot. Recognizing yours early helps you prepare better and avoid the mistakes that often derail people on this path."
}];

const deeperAssessmentAreas = [
{
  title: "Your motivation",
  text: "Why you are drawn toward entrepreneurship in the first place."
},
{
  title: "Your stability",
  text: "Whether your life circumstances support a move into entrepreneurship."
},
{
  title: "Your skills",
  text: "The strengths you bring as a potential founder."
},
{
  title: "Your strategic direction",
  text: "What kind of business path might suit you."
},
{
  title: "How you operate",
  text: "How you approach decisions, uncertainty, and execution."
}];

const pivotbox5sImage = "/assets/pivotbox/s5-assessment.png";

const whoThisIsForCards = [
  {
    id: "01",
    quote: "I have been in corporate for over a decade. The pull toward building something of my own keeps coming back — and this time it feels different.",
    chip: "The pull is real",
  },
  {
    id: "02",
    quote: "I have an idea — maybe several. But I am not sure if now is the right time, or if I am actually the right person to do this.",
    chip: "The doubt is normal",
  },
  {
    id: "03",
    quote: "By every external measure, things are going well. But something about it no longer feels like enough.",
    chip: "The ceiling is showing",
  },
  {
    id: "04",
    quote: "I want to move — but I want to move with structure and clarity, not on impulse or because I had a bad week at work.",
    chip: "The instinct is good",
  },
];

const suhasPortraitImage = "/assets/pivotbox/founder-suhas.jpeg";

const founderStorySections = [
  {
    heading: "The corporate years",
    paragraphs: [founderStory[0]],
  },
  {
    heading: "The leap into entrepreneurship",
    paragraphs: [founderStory[1]],
  },
  {
    heading: "The real lesson",
    paragraphs: [founderStory[2], founderStory[3], founderStory[4]],
  },
  {
    heading: "Why Pivotbox exists",
    paragraphs: [founderStory[5], founderStory[6]],
  },
];

const journeySteps = [
  {
    stage: "S0",
    eyebrow: "Self assessment",
    title: "Discover your pivot type",
    body: "Start with a Founder Signal test to understand what is driving your interest in entrepreneurship. In just 3 minutes, you get a clear reflection on your current motivation and the kind of founder you are likely to be.",
    chip: "→ Your pivot type · Seeker · Evolver · Aspirer · Navigator",
  },
  {
    stage: "S1 + S2",
    eyebrow: "Founder diagnostic",
    title: "Understand yourself as a founder",
    body: "If you choose to go deeper, Pivotbox helps you look at yourself honestly as a potential founder. This covers four things:",
    bullets: [
      "Whether your current life stage and finances can support a transition",
      "The natural strengths and blind spots you bring as a founder",
      "How you respond to uncertainty, pressure, and risk",
      "Which types of businesses are most likely to suit how you operate",
    ],
    chip: "→ Founder diagnostic report",
  },
  {
    stage: "S3",
    eyebrow: "Strategic clarity",
    title: "Explore the right direction",
    body: "With clarity about yourself, the next step is understanding what kind of business actually makes sense for you. This is where your thinking gets structured — market fit, revenue model, and a realistic path forward.",
    chip: "→ Strategic venture blueprint",
  },
  {
    stage: "S4",
    eyebrow: "Founder operating system",
    title: "Move towards action",
    body: "The final step translates your clarity into a practical first move. Whether that is a 90-day experiment, building a portable vehicle while still employed, or designing a structured exit — you leave with a specific action, not just a decision.",
    chip: "→ Your founder operating framework",
  },
];

const problemSectionQuestions = [
  "Am I ready to leave my job?",
  "What kind of business actually suits me?",
  "Should I experiment first, or go all in?",
  "What if I fail?",
  "Do I have what it takes to sustain this?"
];

const programmeSteps = [
  { id: "S0", title: "Founder\nSignal\nTest", keyword: "SELF", keywordColor: "#2F7D57", active: true },
  { id: "S1", title: "Founder\nReality\nCheck", keyword: "STABILITY", keywordColor: "#1565C0" },
  { id: "S2", title: "Founder\nDiagnostic", keyword: "SKILL", keywordColor: "#1565C0" },
  { id: "S3", title: "Founder\nStrategy\nBlueprint", keyword: "STRATEGY", keywordColor: "#7B1FA2" },
  { id: "S4", title: "Founder\nOperating\nSystem", keyword: "SHIFT", keywordColor: "#7B1FA2" },
];

const fiveS_ModelCards = [
  {
    id: "S0", color: "#2F7D57", keyword: "SELF", keywordColor: "#d98825",
    title: "Founder Signal Test",
    desc: "Discover what is driving your entrepreneurial interest and your natural founder orientation.",
    badge: "FREE · START HERE", badgeBg: "#edf5e8", badgeColor: "#2F7D57",
  },
  {
    id: "S1", color: "#1565C0", keyword: "STABILITY", keywordColor: "#d98825",
    title: "Founder Reality Check",
    desc: "Is your life structurally positioned to support this move right now — or are there things to fix first?",
    badge: "READINESS", badgeBg: "#e3f2fd", badgeColor: "#1565C0",
  },
  {
    id: "S2", color: "#d98825", keyword: "SKILL", keywordColor: "#d98825",
    title: "Founder Diagnostic",
    desc: "What kind of founder are you, how durable are you under pressure, and what can you actually deliver.",
    badge: "3 ASSESSMENTS", badgeBg: "#fff3e0", badgeColor: "#d98825",
  },
  {
    id: "S3", color: "#2F7D57", keyword: "STRATEGY", keywordColor: "#d98825",
    title: "Founder Strategy Blueprint",
    desc: "Does your idea work as a business — and what would it actually take to build it?",
    badge: "ADVISORY SESSION", badgeBg: "#edf5e8", badgeColor: "#2F7D57",
  },
  {
    id: "S4", color: "#7B1FA2", keyword: "SHIFT", keywordColor: "#d98825",
    title: "Founder Operating System",
    desc: "Once you commit, how do you operate week to week without losing direction or burning out?",
    badge: "ADVISORY SESSION", badgeBg: "#f3e5f5", badgeColor: "#7B1FA2",
  },
];

const howItWorksSteps = [
  {
    id: "S0", badgeColor: "#B8E0CA",
    eyebrow: "SELF ASSESSMENT · FREE",
    title: "Founder Signal Test",
    desc: "Understand what is driving your interest in entrepreneurship — and what your founder orientation reveals about how you are likely to approach the transition. Takes 3 minutes. Immediate result.",
    chip: "FREE · START HERE", chipBg: "#2F7D57", chipColor: "#ffffff",
  },
  {
    id: "S1", badgeColor: "#BBDEFB",
    eyebrow: "FOUNDER DIAGNOSTIC",
    title: "Founder Reality Check",
    desc: "Is your life structurally positioned to support this move right now — or are there things to fix first? Your finances, your family environment, your working patterns. This is the first layer most people skip — and the first one that determines whether the transition succeeds.",
    chip: "STRUCTURAL READINESS · 24 STATEMENTS", chipBg: "#edf5e8", chipColor: "#2F7D57",
  },
  {
    id: "S2", badgeColor: "#FFE0B2",
    eyebrow: "FOUNDER DIAGNOSTIC",
    title: "What kind of founder are you",
    desc: "Three assessments that go deeper than any personality test — your Natural Founder Archetype (how you operate when there is no structure), your Founder Durability Score (whether you can stay in the game through the hard months), and your Capability & Execution Profile (what you actually do week after week).",
    chip: "3 ASSESSMENTS · 75 STATEMENTS", chipBg: "#fff3e0", chipColor: "#d98825",
  },
  {
    id: "S3", badgeColor: "#C8E6C9",
    eyebrow: "ADVISORY SESSION",
    title: "Founder Strategy Blueprint",
    desc: "With your diagnostic results in hand, this session works through your business idea using a structured 6-layer framework — market reality, revenue model, competitive landscape, what running it actually involves, funding approach, and your specific transition path.",
    chip: "45-MIN ADVISORY SESSION · NOT AN ASSESSMENT", chipBg: "#edf5e8", chipColor: "#2F7D57",
  },
  {
    id: "S4", badgeColor: "#E1BEE7",
    eyebrow: "ADVISORY SESSION",
    title: "Founder Operating System",
    desc: "Once you commit, how do you operate week to week without losing direction or burning out? Your personal operating system, how to track what matters, how to make decisions under pressure, how to maintain focus when everything is pulling you in different directions. Operational discipline, not motivation.",
    chip: "45-MIN ADVISORY SESSION · NOT AN ASSESSMENT", chipBg: "#f3e5f5", chipColor: "#7B1FA2",
  },
];


export default function PivotboxLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDialogOpen = location.pathname === "/discover-your-pivot";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    // Lock body scroll when mobile drawer is open
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('[data-reveal="true"]'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      {
        threshold: 0.16
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(location.hash.replace("#", ""));
      if (target) {
        window.setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
      }
    }
  }, [location.hash]);

  const openDiscovery = () => navigate("/discover-your-pivot");
  const onDialogChange = (nextOpen) => navigate(nextOpen ? "/discover-your-pivot" : "/");

  return (
    <div className="pivotbox-shell" data-testid="pivotbox-landing-page">
      <header className="site-header" data-testid="site-header">
        <div className="pivotbox-container site-header__inner">
          <Link className="site-logo" data-testid="site-logo-link" to="/">
            <span className="site-logo__mark" data-testid="site-logo-mark">
              <img alt="Pivotbox brand mark" className="site-logo__mark-image" data-testid="site-logo-mark-image" src={brandAssets.mark} />
            </span>
            <span className="site-logo__wordmark" data-testid="site-logo-text">
              <img alt="The Pivotbox wordmark" className="site-logo__wordmark-image" data-testid="site-logo-wordmark-image" src={brandAssets.wordmark} />
            </span>
          </Link>

          <div className="site-nav" data-testid="site-nav">
            <nav className="site-nav__links" data-testid="site-nav-links">
              {sectionLinks.filter((item) => !item.cta).map((item) =>
              <a
                data-testid={`site-nav-link-${item.href}`}
                href={`#${item.href}`}
                key={item.href}
                className={item.href === "site-footer" ? "site-nav__link--cta" : undefined}
              >
                  {item.label}
                </a>
              )}
            </nav>
            <button
              type="button"
              className="site-nav__toggle"
              aria-label="Open menu"
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => setMobileNavOpen(true)}
              data-testid="site-nav-mobile-toggle"
            >
              <Menu size={26} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      <div
        id="mobile-nav-drawer"
        className={`mobile-nav ${mobileNavOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileNavOpen}
        data-testid="mobile-nav-drawer"
      >
        <button
          type="button"
          className="mobile-nav__backdrop"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
          tabIndex={mobileNavOpen ? 0 : -1}
          data-testid="mobile-nav-backdrop"
        />
        <div className="mobile-nav__panel" data-testid="mobile-nav-panel">
          <div className="mobile-nav__header">
            <span className="mobile-nav__title">Menu</span>
            <button
              type="button"
              className="mobile-nav__close"
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
              data-testid="mobile-nav-close"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>
          <nav className="mobile-nav__links" data-testid="mobile-nav-links">
            {sectionLinks.map((item) =>
              item.cta ? (
                <Link
                  key={item.href}
                  to="/foundersignaltest"
                  className="mobile-nav__link mobile-nav__link--cta"
                  onClick={() => setMobileNavOpen(false)}
                  data-testid={`mobile-nav-link-${item.href}`}
                >
                  <span>{item.label}</span>
                  <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={`#${item.href}`}
                  className="mobile-nav__link"
                  onClick={() => setMobileNavOpen(false)}
                  data-testid={`mobile-nav-link-${item.href}`}
                >
                  <span>{item.label}</span>
                  <ArrowRight size={16} aria-hidden="true" className="mobile-nav__chev" />
                </a>
              )
            )}
          </nav>
        </div>
      </div>

      <main data-testid="site-main-content">
        <section className="section-shell hero-section-v2" data-reveal="true" data-testid="hero-section" id="top">
          <div className="pivotbox-container section-grid hero-grid">
            <div className="span-7 hero-copy" data-testid="hero-copy-column">
              <h1 className="section-title hero-title-v2" data-testid="hero-title">
                Entrepreneurship is not just a decision.
                <span className="hero-title-v2__italic">It is a journey that requires preparation.</span>
              </h1>
              <p className="section-description hero-desc-v2" data-testid="hero-subtitle">
                At some point, most professionals think about starting something of their own. The idea stays but the next step is never clear.
              </p>
              <p className="section-description hero-desc-v2" data-testid="hero-subtitle-2">
                Pivotbox gives you that next step. A structured way to understand where you stand today, what's holding you back, and what you need to do before you make the move.
              </p>

              <div data-testid="hero-cta-group">
                <Link className="pivotbox-button hero-cta-v2" data-testid="hero-cta-button" to="/foundersignaltest">
                  Take the Founder signal test — Free
                  <ArrowRight size={18} />
                </Link>
              </div>

              <div className="hero-meta-line" data-testid="hero-meta-line">
                <span className="hero-meta-check"><Check size={14} /></span>
                Free <span className="hero-meta-dot">·</span> Takes 3 minutes <span className="hero-meta-dot">·</span> No sign-up required
              </div>
            </div>

            <div className="span-5" data-testid="hero-visual-column">
              <div className="model-card-v2" data-testid="programme-card">
                <div className="model-card-v2__header" data-testid="programme-card-header">THE PIVOTBOX 5S MODEL</div>
                <div className="model-card-v2__flow" data-testid="programme-card-list">
                  {programmeSteps.map((step, index) =>
                    <React.Fragment key={step.id}>
                      <div className={`model-card-v2__step ${step.active ? 'model-card-v2__step--active' : ''}`} data-testid={`programme-step-${index}`}>
                        {step.active && <span className="model-card-v2__free-tag">FREE</span>}
                        <span className="model-card-v2__id">{step.id}</span>
                        <span className="model-card-v2__title">{step.title}</span>
                        <span className="model-card-v2__keyword" style={{ color: step.keywordColor }}>{step.keyword}</span>
                      </div>
                      {index < programmeSteps.length - 1 && <span className="model-card-v2__arrow">→</span>}
                    </React.Fragment>
                  )}
                </div>
                <div className="model-card-v2__footer" data-testid="programme-card-footer">
                  <span className="model-card-v2__footer-text">A structured end-to-end programme for founder transition.</span>
                  <span className="model-card-v2__footer-green">Start free with S0.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell problem-section-v2" data-reveal="true" data-testid="problem-section">
          <div className="pivotbox-container">
            <div className="section-grid problem-layout" data-testid="problem-layout">
              <div className="span-7" data-testid="problem-copy-column">
                <span className="problem-v2-label" data-testid="problem-eyebrow">
                  THE PROBLEM
                </span>
                <h2 className="problem-v2-title" data-testid="problem-title">
                  Most people approach entrepreneurship
                  <span className="problem-v2-title__italic">without a structured plan.</span>
                </h2>

                <div className="problem-v2-quote" data-testid="problem-closing-copy">
                  <p className="problem-v2-quote__text" data-testid="problem-closing-line-1">
                    "Without clarity, the decision becomes confusing — and most people either leap too fast, or never move at all."
                  </p>
                </div>

                <p className="problem-v2-body" data-testid="problem-description">
                  Many professionals feel the pull toward entrepreneurship strongly. But the questions that follow are rarely answered systematically before the leap is made. That is why most of them struggle in year one — not because they lacked talent or ambition, but because they never had a structured framework for the transition.
                </p>
              </div>

              <div className="span-5" data-testid="problem-visual-column">
                <div className="problem-v2-question-rail" data-testid="problem-question-grid">
                  {problemSectionQuestions.map((question, index) =>
                    <div className="problem-v2-question-row" data-testid={`problem-question-card-${index + 1}`} key={question}>
                      <span className="problem-v2-question-icon" data-testid={`problem-question-icon-${index + 1}`}>
                        ?
                      </span>
                      <p className="problem-v2-question-text" data-testid={`problem-question-text-${index + 1}`}>
                        {question}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell" data-reveal="true" data-testid="audience-section">
          <div className="pivotbox-container">
            <div className="section-header--center who-this-is-for-header" data-testid="audience-header">
              <span className="section-label" data-testid="audience-eyebrow">
                Who this is for
              </span>
              <h2 className="section-title section-title--medium who-this-is-for-title" data-testid="audience-title">
                You are not alone in feeling this way.
              </h2>
              <p className="who-this-is-for-intro" data-testid="audience-intro">
                Most professionals who think about entrepreneurship never act on it — not because they lack capability, but because they never found the clarity to move with confidence. That is exactly the gap Pivotbox is built for.
              </p>
            </div>

            <div className="who-this-is-for-grid" data-testid="audience-grid">
              {whoThisIsForCards.map((item, index) =>
              <div className="who-this-is-for-card" data-testid={`audience-card-${index + 1}`} key={item.id}>
                  <div className="who-this-is-for-card__index" data-testid={`audience-card-index-${index + 1}`}>
                    {item.id}
                  </div>
                  <p className="who-this-is-for-card__quote" data-testid={`audience-text-${index + 1}`}>
                    “{item.quote}”
                  </p>
                  <div className="who-this-is-for-card__chip" data-testid={`audience-chip-${index + 1}`}>
                    {item.chip}
                  </div>
                </div>
              )}
            </div>

            <div className="section-header--center mt-6" data-testid="audience-cta-block">
              <Link className="pivotbox-button" data-testid="audience-cta-button" to="/foundersignaltest">
                Take the Founder signal test - Free
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Simple solution → CTA-only section (replaces the old in-page assessment) */}
        <section className="section-shell founder-signal-section founder-signal-section--cta" data-reveal="true" data-testid="founder-signal-cta-section">
          <div className="pivotbox-container">
            <div className="founder-signal-cta-card" data-testid="founder-signal-cta-card">
              <span className="founder-signal-label dm-mono" data-testid="founder-signal-cta-eyebrow">
                THE SIMPLE SOLUTION
              </span>
              <h2 className="founder-signal-title founder-signal-cta-title" data-testid="founder-signal-cta-title">
                Start with a simple assessment.
              </h2>
              <p className="founder-signal-body founder-signal-cta-body" data-testid="founder-signal-cta-body">
                The Founder Signal Assessment helps you discover the motivation behind your entrepreneurial thinking and what it means for your next step. Rate 15 honest statements &mdash; no right or wrong answers, just a mirror held up to where you actually are.
              </p>
              <Link
                className="pivotbox-button founder-signal-cta-button"
                data-testid="founder-signal-cta-button"
                to="/foundersignaltest"
              >
                Take the Founder Signal Test &mdash; Free
                <ArrowRight size={18} />
              </Link>
              <div className="founder-signal-cta-meta" data-testid="founder-signal-cta-meta">
                &#10003; Free &nbsp;&middot;&nbsp; 5 minutes &nbsp;&middot;&nbsp; Instant report
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell" data-reveal="true" data-testid="what-pivotbox-does-section">
          <div className="pivotbox-container">
            <div className="section-header--center" data-testid="what-pivotbox-does-header">
              <span className="section-label" data-testid="what-pivotbox-does-eyebrow">
                What the Test Reveals
              </span>
              <h2 className="section-title section-title--medium" data-testid="what-pivotbox-does-title">
                What you will discover
              </h2>
              <p className="section-description" data-testid="what-pivotbox-does-description">
                The test helps you understand what may really be driving your interest in entrepreneurship and what it might mean for your next step.
              </p>
            </div>

            <div className="discover-grid" data-testid="what-pivotbox-does-visual-frame">
              {testRevealInsights.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div className="discover-card" data-testid={`what-pivotbox-does-alignment-card-${index + 1}`} key={item.title}>
                    <span className="discover-card__icon" data-testid={`what-pivotbox-does-alignment-icon-${index + 1}`}>
                      <Icon size={24} />
                    </span>
                    <h3 className="comparison-card__title" data-testid={`what-pivotbox-does-alignment-title-${index + 1}`}>
                      {item.title}
                    </h3>
                    <p className="alignment-card__text" data-testid={`what-pivotbox-does-alignment-text-${index + 1}`}>
                      {item.text}
                    </p>
                  </div>);

              })}
            </div>

            <div className="discover-closing-line" data-testid="what-pivotbox-does-alignment-summary">
              Sometimes a small moment of reflection is all it takes to see your next step more clearly.
            </div>

              <div className="section-header--center mt-6" data-testid="what-pivotbox-does-cta-block">
              <Link className="pivotbox-button" data-testid="what-pivotbox-does-cta-button" to="/foundersignaltest">
                Take the Founder signal test - Free
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══ WHAT COMES NEXT ═══ */}
        <section className="what-comes-next" data-testid="what-comes-next-section">
          <div className="pivotbox-container what-comes-next__inner">
            <div className="what-comes-next__left">
              <span className="what-comes-next__eyebrow">WHAT COMES NEXT</span>
              <h2 className="what-comes-next__headline">
                The Founder<br />Signal Test<br />is the beginning,<br />
                <em className="what-comes-next__italic">not the answer.</em>
              </h2>
            </div>
            <div className="what-comes-next__right">
              <div className="what-comes-next__card">
                <p className="what-comes-next__card-text">
                  Now that you know your motivation to become an entrepreneur, you can go deeper to understand the <span className="what-comes-next__orange">other aspects that determine whether you will succeed.</span>
                </p>
                <div className="what-comes-next__card-divider" />
                <p className="what-comes-next__card-text">
                  All of this is inside the <span className="what-comes-next__orange">5S model</span> — a structured path from where you are today to a business you can actually build.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell fivesmodel-section-v2" data-reveal="true" data-testid="framework-section" id="framework">
          <div className="pivotbox-container">
            <div className="fivesmodel-v2__header">
              <span className="fivesmodel-v2__label" data-testid="framework-eyebrow">THE FRAMEWORK</span>
              <h2 className="fivesmodel-v2__title" data-testid="framework-title">The Pivotbox 5S Model</h2>
              <p className="fivesmodel-v2__subtitle" data-testid="framework-description">
                A structured end-to-end framework — from your first signal of entrepreneurial intent to a fully operationalised transition plan. Each step builds on the previous one.
              </p>
            </div>

            <div className="fivesmodel-v2__grid" data-testid="framework-card-grid">
              {fiveS_ModelCards.map((card, index) =>
                <React.Fragment key={card.id}>
                  <div className="fivesmodel-v2__card" data-testid={`framework-card-${index + 1}`}>
                    <div className="fivesmodel-v2__card-bar" style={{ background: card.color }} />
                    <div className="fivesmodel-v2__card-body">
                      <div className="fivesmodel-v2__card-top">
                        <span className="fivesmodel-v2__card-id">{card.id}</span>
                        <span className="fivesmodel-v2__card-keyword" style={{ color: card.keywordColor }}>{card.keyword}</span>
                      </div>
                      <h3 className="fivesmodel-v2__card-title" data-testid={`framework-card-title-${index + 1}`}>{card.title}</h3>
                      <p className="fivesmodel-v2__card-desc">{card.desc}</p>
                      {card.id === "S0" ? (
                        <Link to="/foundersignaltest" className="fivesmodel-v2__card-badge fivesmodel-v2__card-badge--cta" style={{ background: card.badgeBg, color: card.badgeColor }}>{card.badge}</Link>
                      ) : (
                        <span className="fivesmodel-v2__card-badge" style={{ background: card.badgeBg, color: card.badgeColor }}>{card.badge}</span>
                      )}
                    </div>
                  </div>
                  {index < fiveS_ModelCards.length - 1 && <span className="fivesmodel-v2__arrow">→</span>}
                </React.Fragment>
              )}
            </div>
          </div>
        </section>

        <section className="section-shell hiw-section-v2" data-reveal="true" data-testid="how-it-works-section" id="how-it-works">
          <div className="pivotbox-container">
            <div className="section-grid hiw-v2__header-grid">
              <div className="span-5" data-testid="how-it-works-header">
                <span className="hiw-v2__label" data-testid="how-it-works-eyebrow">HOW IT WORKS</span>
                <h2 className="hiw-v2__title" data-testid="how-it-works-title">
                  Each step builds <span className="hiw-v2__title-italic">on the last.</span>
                </h2>
              </div>
              <div className="span-7" data-testid="how-it-works-intro">
                <p className="hiw-v2__intro-text" data-testid="how-it-works-process-text">
                  The Pivotbox 5S model is designed as an integrated system. Strategy is not created in isolation, it emerges from your readiness and capabilities. Execution is not improvised, it is built on clear direction. Each step connects to the next, forming a complete entrepreneurial journey.
                </p>
              </div>
            </div>

            <div className="hiw-v2__timeline" data-testid="how-it-works-process-grid">
              {howItWorksSteps.map((step, index) =>
                <div className="hiw-v2__step" data-testid={`how-it-works-process-item-${index + 1}`} key={step.id}>
                  <div className="hiw-v2__step-badge" style={{ background: step.badgeColor }} data-testid={`how-it-works-badge-${index + 1}`}>
                    <span className="hiw-v2__step-badge-text">{step.id}</span>
                  </div>
                  <div className="hiw-v2__step-content">
                    <div className="hiw-v2__step-eyebrow" data-testid={`how-it-works-eyebrow-${index + 1}`}>{step.eyebrow}</div>
                    <h3 className="hiw-v2__step-title" data-testid={`how-it-works-title-${index + 1}`}>{step.title}</h3>
                    <p className="hiw-v2__step-desc" data-testid={`how-it-works-desc-${index + 1}`}>{step.desc}</p>
                    {index === 0 ? (
                      <Link to="/foundersignaltest" className="hiw-v2__step-chip hiw-v2__step-chip--cta" style={{ background: step.chipBg, color: step.chipColor }} data-testid={`how-it-works-chip-${index + 1}`}>{step.chip}</Link>
                    ) : (
                      <span className="hiw-v2__step-chip" style={{ background: step.chipBg, color: step.chipColor }} data-testid={`how-it-works-chip-${index + 1}`}>{step.chip}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── SAMPLE REPORT ───────────────────────────────────────── */}
        <section className="section-shell sample-report-section" data-reveal="true" data-testid="sample-report-section" id="sample-report">
          <div className="pivotbox-container">
            <div className="sample-report-grid">
              <div className="sample-report-copy" data-testid="sample-report-copy">
                <span className="sample-report-eyebrow" data-testid="sample-report-eyebrow">SAMPLE REPORT</span>
                <h2 className="sample-report-title" data-testid="sample-report-title">
                  See the actual report
                  <span className="sample-report-title__italic">before you begin.</span>
                </h2>
                <p className="sample-report-lede" data-testid="sample-report-lede">
                  This is a real Founder Signal Test result, not a summary or a teaser. Download the full 7-page PDF and read it before you take the test. You will know exactly what you are signing up for.
                </p>
                <ul className="sample-report-list" data-testid="sample-report-list">
                  <li><strong>Your dominant signal decoded, not just named.</strong> What drives you, why it matters, and what your specific blind spot is.</li>
                  <li><strong>All four profile scores shown and interpreted,</strong> including what the secondary signals mean in context.</li>
                  <li><strong>The questions you need to be asking yourself</strong> before you start your entrepreneurship journey, laid out clearly so you know what to examine before you decide.</li>
                </ul>
              </div>

              {/* Mock report card on the right */}
              <div className="sample-report-mock" data-testid="sample-report-mock" aria-hidden="true">
                <div className="sample-report-mock__top">
                  <div className="sample-report-mock__brand">
                    <img src="/pivotbox-p-logo.png" alt="" className="sample-report-mock__logo" />
                    <span className="sample-report-mock__wordmark">Pivotbox</span>
                  </div>
                  <span className="sample-report-mock__tag">Founder Signal Result &middot; Confidential</span>
                </div>
                <div className="sample-report-mock__body">
                  <span className="sample-report-mock__eyebrow">YOUR DOMINANT SIGNAL</span>
                  <div className="sample-report-mock__hero">
                    <span className="sample-report-mock__hero-eyebrow">YOUR FOUNDER SIGNAL TYPE</span>
                    <div className="sample-report-mock__hero-name">The Aspirer</div>
                    <p className="sample-report-mock__hero-tagline"><em>The pull toward building. You want to build something that counts.</em></p>
                    <div className="sample-report-mock__hero-score">
                      <span className="sample-report-mock__score-num">22</span>
                      <span className="sample-report-mock__score-den">/25</span>
                    </div>
                    <div className="sample-report-mock__score-track">
                      <div className="sample-report-mock__score-fill" style={{ width: "88%" }} />
                    </div>
                  </div>
                  <div className="sample-report-mock__bars">
                    {[
                      { label: "Aspirer",   value: 22, color: "#5B2D8E" },
                      { label: "Navigator", value: 16, color: "#1E4D8C" },
                      { label: "Evolver",   value: 14, color: "#1B6B5A" },
                      { label: "Seeker",    value: 12, color: "#8B2A1A" },
                    ].map((d) => (
                      <div className="sample-report-mock__bar-row" key={d.label}>
                        <span className="sample-report-mock__bar-label" style={{ color: d.color }}>{d.label}</span>
                        <span className="sample-report-mock__bar-track">
                          <span className="sample-report-mock__bar-fill" style={{ width: `${(d.value / 25) * 100}%`, background: d.color }} />
                        </span>
                        <span className="sample-report-mock__bar-val">{d.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="sample-report-mock__footer">
                    <span>Confidential</span>
                    <span className="sample-report-mock__footer-pill">7 pages</span>
                  </div>
                </div>
              </div>

              {/* CTA block — sits below the copy on desktop, after the mock on mobile */}
              <div className="sample-report-cta-block" data-testid="sample-report-cta-block">
                <a
                  href="/sample_s0_report.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pivotbox-button sample-report-cta"
                  data-testid="sample-report-download-btn"
                >
                  Download sample report &nbsp;&rarr;
                </a>
                <p className="sample-report-meta" data-testid="sample-report-meta">PDF · 7 pages · No email required</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BUILT ON SCIENCE ──────────────────────────────────── */}
        <BuiltOnScienceSection variant="compact" />

        {/* ── VALIDATED BY ENTREPRENEURS ──────────────────────────── */}
        <section className="section-shell validated-section" data-reveal="true" data-testid="validated-section" id="validated">
          <div className="pivotbox-container">
            <div className="validated-section__header" data-testid="validated-header">
              <span className="validated-section__eyebrow" data-testid="validated-eyebrow">
                <span className="validated-section__eyebrow-line" aria-hidden="true" />
                <span className="validated-section__eyebrow-label">VALIDATED BY ENTREPRENEURS</span>
                <span className="validated-section__eyebrow-line" aria-hidden="true" />
              </span>
              <h2 className="validated-section__title" data-testid="validated-title">
                50+ entrepreneurs ran through this model.<br />They agreed it asks the right questions.
              </h2>
              <p className="validated-section__lede" data-testid="validated-lede">
                What came back was consistent &mdash; the five stages reflect the questions they wished someone had made them answer &mdash; before they made the move.
              </p>
              <div className="validated-section__pills" data-testid="validated-pills">
                {[
                  { tag: "S0", word: "Self",      color: "#d98825" },
                  { tag: "S1", word: "Stability", color: "#d98825" },
                  { tag: "S2", word: "Skill",     color: "#d98825" },
                  { tag: "S3", word: "Strategy",  color: "#d98825" },
                  { tag: "S4", word: "Shift",     color: "#d98825" },
                ].map((p) => (
                  <span className="validated-pill" data-testid={`validated-pill-${p.tag}`} key={p.tag}>
                    <span className="validated-pill__tag" style={{ color: p.color }}>{p.tag}</span>
                    <span className="validated-pill__word">{p.word}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="validated-section__grid" data-testid="validated-quotes">
              {[
                { quote: "I wish I had done this before I quit. The structural questions in S1 are exactly the ones nobody thinks to ask \u2014 until it is too late.", role: "FOUNDER, B2B SERVICES" },
                { quote: "The archetype result was more accurate than anything I have done before. It named exactly how I build \u2014 and the blind spot it flagged turned out to be real.", role: "FOUNDER, TECHNOLOGY" },
                { quote: "The sequence makes sense. You need to know you are ready before you figure out how you build \u2014 not the other way around. That ordering alone is different from everything else out there.", role: "FOUNDER, D2C" },
              ].map((q, i) => (
                <article className="validated-quote-card" data-testid={`validated-quote-card-${i + 1}`} key={i}>
                  <span className="validated-quote-card__mark" aria-hidden="true">&ldquo;</span>
                  <p className="validated-quote-card__quote">{q.quote}</p>
                  <p className="validated-quote-card__role">{q.role}</p>
                </article>
              ))}
            </div>

            <div className="validated-section__cta-wrap" data-testid="validated-cta-wrap">
              <Link to="/foundersignaltest" className="validated-section__cta" data-testid="validated-take-test-cta">
                Take the Founder Signal Test &mdash; Free &nbsp;&rarr;
              </Link>
            </div>
          </div>
        </section>

        <section className="section-shell" data-reveal="true" data-testid="about-section" id="about">
          <div className="pivotbox-container section-grid founder-layout">
            <div className="span-5" data-testid="founder-photo-column">
              <div className="founder-summary-panel" data-testid="founder-photo-frame">
                <div className="founder-summary-panel__header" data-testid="founder-summary-header">
                  <img alt="Suhas Mahaveer portrait" className="founder-summary-panel__image" data-testid="founder-summary-image" src={suhasPortraitImage} />
                  <div className="founder-summary-panel__eyebrow" data-testid="founder-summary-eyebrow">
                    Founder credibility
                  </div>
                  <h3 className="founder-summary-panel__title" data-testid="founder-summary-title">
                    Suhas Mahaveer
                  </h3>
                  <p className="founder-summary-panel__text" data-testid="founder-summary-text">
                    Senior Management professional, Business Coach, Entrepreneur who built the Pivotbox framework from lived founder experience.
                  </p>
                </div>

                <div className="founder-highlight-grid" data-testid="founder-highlight-grid">
                  {founderHighlights.map((item, index) =>
                  <div className="founder-highlight-card" data-testid={`founder-highlight-card-${index + 1}`} key={item.value}>
                      <div className="founder-highlight-card__value" data-testid={`founder-highlight-value-${index + 1}`}>
                        {item.value}
                      </div>
                      <div className="founder-highlight-card__label" data-testid={`founder-highlight-label-${index + 1}`}>
                        {item.label}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="span-7" data-testid="founder-story-column">
              <span className="section-label" data-testid="founder-eyebrow">
                About the creator
              </span>
              <h2 className="section-title section-title--medium" data-testid="founder-title">
                From corporate to founder - the hard way
              </h2>
              <div className="founder-story" data-testid="founder-story-content">
                {founderStorySections.map((section, sectionIndex) =>
                <div className="founder-story-block" data-testid={`founder-story-block-${sectionIndex + 1}`} key={section.heading}>
                    <h3 className="founder-story-block__title" data-testid={`founder-story-block-title-${sectionIndex + 1}`}>
                      {section.heading}
                    </h3>
                    {section.paragraphs.map((paragraph, paragraphIndex) =>
                    <p className="body-copy founder-story-paragraph" data-testid={`founder-story-paragraph-${sectionIndex + 1}-${paragraphIndex + 1}`} key={paragraph}>
                        {paragraph}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="quote-card" data-testid="founder-quote-card">
                <h3 className="comparison-card__title" data-testid="founder-quote-title">
                  A founder principle behind Pivotbox
                </h3>
                <p className="quote-copy" data-testid="founder-quote-text">
                  “{quote}”
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell next-move-section-v2" data-reveal="true" data-testid="final-cta-section">
          <div className="pivotbox-container">
            <div className="section-grid next-move-v2__grid">
              <div className="span-5" data-testid="next-move-copy-column">
                <span className="next-move-v2__label" data-testid="final-cta-eyebrow">YOUR NEXT MOVE</span>
                <h2 className="next-move-v2__title" data-testid="final-cta-title">
                  Most people think about this for months.
                  <span className="next-move-v2__title-italic">The ones who move</span>
                  start with one small step.
                </h2>
                <p className="next-move-v2__body" data-testid="final-cta-description">
                  The Founder Signal Test takes 3 minutes. What you discover in those 3 minutes becomes the starting point for understanding what entrepreneurship could genuinely look like for you — specifically.
                </p>
              </div>

              <div className="span-7" data-testid="next-move-action-column">
                <div className="next-move-v2__cards">
                  <div className="next-move-v2__card" data-testid="next-move-card-1">
                    <span className="next-move-v2__card-num">1</span>
                    <div className="next-move-v2__card-content">
                      <h3 className="next-move-v2__card-title">Take the Founder Signal Test</h3>
                      <p className="next-move-v2__card-desc">Answer 15 honest statements. Discover your Founder Signal Type and what it says about your entrepreneurial energy right now.</p>
                      <span className="next-move-v2__card-chip">FREE · 3 MINUTES · NO SIGN-UP</span>
                    </div>
                  </div>
                  <div className="next-move-v2__card" data-testid="next-move-card-2">
                    <span className="next-move-v2__card-num">2</span>
                    <div className="next-move-v2__card-content">
                      <h3 className="next-move-v2__card-title">See your result — and what comes next</h3>
                      <p className="next-move-v2__card-desc">Your result is the beginning of the journey, not the end. From there, the complete programme takes you all the way through to strategy and execution.</p>
                    </div>
                  </div>
                </div>

                <Link className="next-move-v2__cta" data-testid="final-cta-button" to="/foundersignaltest">
                  Take the Founder Signal Test
                  <ArrowRight size={18} />
                </Link>
                <div className="next-move-v2__meta next-move-v2__meta--highlight" data-testid="final-cta-subtext">
                  Free · Instant result · No commitment
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* ═══ FAQ SECTION ═══ */}
        <section className="section-shell faq-section" data-testid="faq-section" id="faq">
          <div className="pivotbox-container">
            <div className="faq-header">
              <span className="faq-eyebrow">FREQUENTLY ASKED QUESTIONS</span>
              <h2 className="faq-title" data-testid="faq-title">Still thinking about it? These might help</h2>
            </div>
            <div className="faq-list" data-testid="faq-list">
              {[
                {
                  q: "What is Pivotbox?",
                  a: "Most corporate professionals who want to start a business ask themselves the same question, am I actually ready for this? Pivotbox gives you the answer. It is a diagnostic system that shows you exactly where you are ready to make the move, where you need to prepare, and what your path forward looks like, so that you cross over with clarity and confidence, not just hope."
                },
                {
                  q: "Who is this for?",
                  a: "Anyone in a corporate, consulting, or freelance role who has seriously asked themselves: could I build something of my own? If that question is live for you, even quietly, Pivotbox helps you explore it honestly and understand exactly what your transition would look like."
                },
                {
                  q: "Why should a working professional consider Pivotbox?",
                  a: "The skills that make you successful in a corporate career are genuinely valuable and they are not the whole picture when it comes to entrepreneurship. There are specific things that building a business requires that most careers simply do not develop. Pivotbox helps you see exactly what those are for you, so your preparation is targeted, your transition is well-timed, and your first year is built on a solid foundation rather than a leap of faith."
                },
                {
                  q: "What is the 5S model?",
                  a: "Think of it as a complete roadmap for one of the most significant transitions of your life. It is Pivotbox's step-by-step framework for the corporate-to-entrepreneur transition. You start with a free signal test to see where you stand today. From there, four more stages take you through your founder profile, psychological readiness, business strategy, and execution system, each one giving you a clearer, more complete picture."
                },
                {
                  q: "What is the Founder Signal Test?",
                  a: "Most people spend years wondering if they are built for entrepreneurship. This test answers that question in 3 minutes for free. Twenty statements. Your honest reactions. It creates your profile that shows exactly where you are on the journey from corporate life to building something of your own and what that means for your next move."
                },
                {
                  q: "Is the Founder Signal Test really free?",
                  a: "Completely free. No credit card. No hidden upgrade. No catch. We believe that the first honest insight about your entrepreneurial potential should cost you nothing because once you see it, everything changes. Three minutes is all it takes to start seeing yourself differently."
                },
                {
                  q: "What happens after I take the test?",
                  a: "You get your Pivot Profile immediately. It tells you where your entrepreneurial energy comes from, what your natural transition style looks like, and what to pay attention to as you move forward. Most people tell us it is the first time they have ever seen their situation described this clearly. Some find it so accurate, it is almost uncomfortable, in the best possible way. Subsequently, you can consider doing a full Pivot Diagnostic to prepare yourself better for your entrepreneurial journey."
                },
                {
                  q: "What is the full Pivotbox diagnostic?",
                  a: "Imagine having a complete map of your transition, your founder archetype, your psychological durability, your practical capabilities, your entrepreneurial fit, and your business direction all in one place. That is what the full diagnostic gives you. Eight personalised reports and a 60-minute advisory session with someone who can help you read the map and plan your route. This is for the professional who is serious about making this transition the right way."
                },
                {
                  q: "How is this different from personality tests?",
                  a: "Personality tests tell you, who you are, how you think. Pivotbox is built specifically for the corporate-to-entrepreneurship transition, it measures the mindset, working style, resilience, and practical capabilities that this particular journey requires. The result is not a personality label. It is a targeted, actionable map of how ready you are for this journey."
                },
                {
                  q: "I have already started my business. Is this still relevant for me?",
                  a: "Yes, and in some ways more so. The diagnostic tells you where the friction you are experiencing is coming from, whether it is a capability gap, a psychological pattern, or a structural mismatch between your profile and your business model. Most founders who are six months in and struggling find the report names exactly what they have been feeling but could not articulate."
                },
                {
                  q: "I am not seriously planning to start a business right now. Is this worth doing?",
                  a: "The Founder Signal Test is free and takes five minutes. Many people who were \"just curious\" discover something specific about how they think that changes how they see themselves professionally, whether or not entrepreneurship is the next step. Start there. The paid diagnostic is for when you want to go deeper."
                },
                {
                  q: "Is my data private?",
                  a: "Completely. Your responses belong to you. They are used only to generate your personalised result and are never shared with anyone. What you discover about yourself stays between you and Pivotbox."
                },
              ].map((item, i) => (
                <details className="faq-item" key={i} data-testid={`faq-item-${i + 1}`}>
                  <summary className="faq-item__question" data-testid={`faq-q-${i + 1}`}>
                    <span className="faq-item__num">{String(i + 1).padStart(2, "0")}</span>
                    {item.q}
                    <span className="faq-item__chevron" />
                  </summary>
                  <p className="faq-item__answer" data-testid={`faq-a-${i + 1}`}>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

      </main>

      <SiteFooter />

      <LeadCaptureDialog onOpenChange={onDialogChange} open={isDialogOpen} />
      <StickyCTA href="/foundersignaltest" label="Take the Founder Signal Test — Free →" />
    </div>);

}